/**
 * Admin Layout
 * ============
 * تخطيط لوحة التحكم
 */

import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation, useTheme } from '../../lib/context/ThemeContext';
import { Sidebar, LogoutButton } from '../../lib/ui/Sidebar';
import { SearchBox } from '../../lib/ui/SearchBox';
import './AdminLayout.css';

// أيقونات SVG
const Icons = {
  dashboard: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  ),
  tenants: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 21h18M9 8h1M9 12h1M9 16h1M14 8h1M14 12h1M14 16h1" />
      <path d="M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16" />
    </svg>
  ),
  users: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  roles: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  reports: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  ),
  settings: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
  bell: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  ),
  menu: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  ),
  globe: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  ),
  coupon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
      <line x1="7" y1="7" x2="7.01" y2="7"/>
    </svg>
  ),
};

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const { language, setLanguage } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // عناصر القائمة الجانبية
  const menuItems = [
    { id: 'dashboard', label: 'لوحة التحكم', icon: Icons.dashboard, path: '/admin/dashboard' },
    { id: 'tenants', label: 'الشركات', icon: Icons.tenants, path: '/admin/tenants' },
    { id: 'ships-review', label: 'مراجعة الأشياب', icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 21c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1 .6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/><path d="M19.38 20A11.6 11.6 0 0 0 21 14l-9-4-9 4c0 2.9.94 5.34 2.81 7.76"/><path d="M19 13V7a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v6"/></svg>), path: '/admin/ships-review' },
    { id: 'users', label: 'المستخدمين', icon: Icons.users, path: '/admin/users' },
    { id: 'roles', label: 'الأدوار والصلاحيات', icon: Icons.roles, path: '/admin/roles' },
    { id: 'registrations', label: 'طلبات التسجيل', icon: Icons.bell, path: '/admin/registrations' },
    { id: 'ratings', label: 'التقييمات', icon: Icons.reports, path: '/admin/ratings' },
    { id: 'tickets', label: 'التذاكر', icon: Icons.bell, path: '/admin/tickets' },
    { id: 'chat', label: 'الدردشة', icon: Icons.bell, path: '/admin/chat' },
    { id: 'reports', label: 'التقارير', icon: Icons.reports, path: '/admin/reports' },
    { id: 'requirements', label: 'متطلبات المنصة', icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><path d="M9 12h6M9 16h4"/></svg>), path: '/admin/requirements' },
    { id: 'platform-coupons', label: 'كوبونات المنصة', icon: Icons.coupon, path: '/admin/platform-coupons' },
    { id: 'settings', label: 'الإعدادات', icon: Icons.settings, path: '/admin/settings' },
  ];

  // تحديد العنصر النشط
  const activeItem = menuItems.find(item => location.pathname.startsWith(item.path))?.id || 'dashboard';

  const handleMenuClick = (item) => {
    navigate(item.path || `/admin/${item.id}`);
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const toggleLanguage = () => {
    setLanguage(language === 'ar' ? 'en' : 'ar');
  };

  return (
    <div className="watrly-admin">
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="watrly-admin__overlay"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <Sidebar
        items={menuItems}
        activeItem={activeItem}
        onItemClick={(item) => {
          handleMenuClick(item);
          setMobileMenuOpen(false);
        }}
        collapsed={collapsed}
        onCollapse={setCollapsed}
        className={mobileMenuOpen ? 'watrly-admin__sidebar--mobile-open' : ''}
        logo={
          <div className="watrly-admin__logo">
            <svg viewBox="0 0 24 24" className="watrly-admin__logo-icon">
              <path
                d="M12 2C12 2 6 10 6 14C6 17.3137 8.68629 20 12 20C15.3137 20 18 17.3137 18 14C18 10 12 2 12 2Z"
                fill="#0ABAB5"
              />
            </svg>
            {!collapsed && <span className="watrly-admin__logo-text">وترلي</span>}
          </div>
        }
        footer={<LogoutButton onClick={handleLogout} collapsed={collapsed} label="تسجيل الخروج" />}
      />

      {/* Main Content */}
      <div className={`watrly-admin__main ${collapsed ? 'watrly-admin__main--expanded' : ''}`}>
        {/* Header */}
        <header className="watrly-admin__header">
          {/* Mobile Menu Button */}
          <button 
            className="watrly-admin__menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {Icons.menu}
          </button>

          <div className="watrly-admin__header-start">
            <SearchBox
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="بحث..."
              size="sm"
              variant="filled"
            />
          </div>

          <div className="watrly-admin__header-end">
            {/* Language Switcher */}
            <button 
              className="watrly-admin__header-btn watrly-admin__lang-btn"
              onClick={toggleLanguage}
              title={language === 'ar' ? 'English' : 'العربية'}
            >
              {Icons.globe}
              <span className="watrly-admin__lang-text">
                {language === 'ar' ? 'EN' : 'ع'}
              </span>
            </button>

            {/* Notifications */}
            <button className="watrly-admin__header-btn">
              {Icons.bell}
              <span className="watrly-admin__notification-badge">3</span>
            </button>

            {/* User Menu */}
            <div className="watrly-admin__user">
              <div className="watrly-admin__user-avatar">
                {user?.full_name?.charAt(0) || 'A'}
              </div>
              <div className="watrly-admin__user-info">
                <span className="watrly-admin__user-name">{user?.full_name || 'المدير'}</span>
                <span className="watrly-admin__user-role">Super Admin</span>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="watrly-admin__content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
